<template>
    <div class="mt-3">
        <div class="flex flex-col gap-3 md:grid md:grid-cols-3 md:items-center">
            <div
                class="flex items-center justify-center md:justify-start gap-2"
            >
                <tooltip-button
                    v-if="
                        selected.length === 1 &&
                        $ability.hasPermission('customLink:Update')
                    "
                    icon="✏️"
                    message="Editar enlace seleccionado"
                    @click="onRowAction({ action: 'edit', item: selected[0] })"
                />
                <tooltip-button
                    v-if="
                        selected.length &&
                        $ability.hasPermission('customLink:Delete')
                    "
                    icon="🗑️"
                    :message="
                        selected.length === 1
                            ? 'Eliminar enlace seleccionado'
                            : `Eliminar ${selected.length} enlaces`
                    "
                    @click="deleteSelected"
                />
            </div>
            <h1 class="text-3xl text-center">
                Enlaces
                <strong class="text-theme-primary">
                    {{ items.length }}
                </strong>
            </h1>
            <div class="flex justify-end gap-2">
                <template v-if="reorderMode">
                    <app-button color="success" @click="saveReorder">
                        Guardar orden 💾
                    </app-button>
                    <app-button color="warn" @click="toggleReorderMode">
                        ⚖️ Salir
                    </app-button>
                </template>
                <template v-else>
                    <app-button
                        v-if="
                            $ability.hasPermission('customLink:Create') &&
                            linksCount < maxLinks
                        "
                        color="success"
                        size="large"
                        @click="openCreateModal"
                    >
                        <span class="hidden md:inline">Nuevo enlace</span>
                        <span class="md:hidden font-bold">+</span>
                    </app-button>
                </template>
            </div>
        </div>

        <div class="w-full mt-4">
            <custom-sortable-table
                :fields="fields"
                :items="reorderMode ? reorderedItems : items"
                :per-page="items.length || 20"
                :row-actions="rowActions"
                :draggable="reorderMode"
                :selectable="!reorderMode"
                select-mode="multi"
                responsive
                no-border-collapse
                hover
                class="w-full"
                items-empty-message="No tienes enlaces personalizados. Crea uno para comenzar."
                @flex-selected="onSelected"
                @row-action="onRowAction"
                @drag-drop="onDragDrop"
            >
                <template #cell(image)="data">
                    <img
                        v-if="data.item.image"
                        :src="data.item.image.url"
                        :alt="data.item.title"
                        class="w-10 h-10 rounded-lg object-cover border border-theme-border"
                    />
                    <span v-else class="text-theme-muted text-sm">—</span>
                </template>

                <template #cell(title)="data">
                    <span class="font-medium">
                        {{ data.item.title }}
                    </span>
                </template>

                <template #cell(url)="data">
                    <a
                        v-if="data.item.url"
                        :href="data.item.url"
                        target="_blank"
                        rel="noopener"
                        class="text-theme-primary hover:underline text-sm break-all"
                    >
                        {{ data.item.url | cut(60) }}
                    </a>
                    <span v-else class="text-theme-muted text-sm">
                        Sin enlace
                    </span>
                </template>

                <template #cell(status)="data">
                    <span v-if="!publishedIds" class="text-sm text-theme-muted">
                        —
                    </span>
                    <span
                        v-else-if="isPublished(data.item)"
                        class="text-sm font-medium text-theme-success"
                    >
                        Publicado
                    </span>
                    <span v-else class="text-sm text-theme-muted">
                        Sin publicar
                    </span>
                </template>

                <template #cell(type)="data">
                    <span class="text-sm">
                        {{
                            data.item.type === "banner"
                                ? "Banner 🖼️"
                                : "Link 🔗"
                        }}
                    </span>
                </template>
            </custom-sortable-table>
        </div>

        <create-custom-link-modal
            v-model="showModal"
            ask-type
            :link="editingItem"
            @created="onLinkCreated"
            @updated="onLinkUpdated"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { publishedLinkIds } from "@/common/lib/published-links";

import CreateCustomLinkModal from "@/components/editor/CreateCustomLinkModal.vue";
import CustomSortableTable from "@/components/utils/CustomSortableTable.vue";
import TooltipButton from "@/components/utils/TooltipButton.vue";

export default {
    name: "CustomLinksIndex",
    components: {
        CreateCustomLinkModal,
        CustomSortableTable,
        TooltipButton
    },
    layout: "app",

    data() {
        return {
            items: [],
            selected: [],
            showModal: false,
            editingItem: null,
            maxLinks: 20,
            reorderMode: false,
            reorderedItems: [],
            publishedIds: null,
            fields: [
                { key: "image", label: "Imagen", sortable: false },
                { key: "title", label: "Título", sortable: true },
                { key: "url", label: "URL", sortable: false },
                { key: "type", label: "Tipo", sortable: true },
                { key: "status", label: "Estado", sortable: false }
            ]
        };
    },

    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        linksCount() {
            return this.items.filter((item) => item.type !== "banner").length;
        },
        rowActions() {
            const actions = [];
            if (this.$ability.hasPermission("customLink:Update")) {
                actions.push({
                    key: "edit",
                    icon: "✏️",
                    label: "Editar"
                });
            }
            if (this.$ability.hasPermission("customLink:Delete")) {
                actions.push({
                    key: "delete",
                    icon: "🗑️",
                    label: "Eliminar"
                });
            }
            return actions;
        }
    },

    async mounted() {
        await this.loadItems();
    },

    methods: {
        ...mapActions("customLinks", [
            "listCustomLinks",
            "deleteCustomLink",
            "updateCustomLinksWeights"
        ]),
        ...mapActions("tenants", ["getCatalogPreview"]),

        async loadItems() {
            // El catálogo va aparte (allSettled) para que, si falla, la tabla
            // cargue igual y solo la columna "Estado" quede en "—".
            const [links, banners, catalog] = await Promise.allSettled([
                this.listCustomLinks({ type: "link" }),
                this.listCustomLinks({ type: "banner" }),
                this.getCatalogPreview()
            ]);

            if (
                links.status === "fulfilled" &&
                banners.status === "fulfilled"
            ) {
                const toItems = (response) =>
                    Array.isArray(response) ? response : response?.items || [];
                this.items = [
                    ...toItems(links.value),
                    ...toItems(banners.value)
                ];
            } else {
                this.items = [];
            }

            this.publishedIds =
                catalog.status === "fulfilled"
                    ? publishedLinkIds(catalog.value?.segments)
                    : null;
        },

        isPublished(item) {
            const ids =
                item.type === "banner"
                    ? this.publishedIds.banner
                    : this.publishedIds.link;
            return ids.has(item.id);
        },

        onSelected(items) {
            this.selected = items;
        },

        async deleteSelected() {
            const count = this.selected.length;
            const result = await this.$swal.fire({
                background: this.backgroundColor,
                title: `¿Eliminar ${
                    count === 1 ? "enlace" : count + " enlaces"
                }?`,
                text:
                    count === 1
                        ? `Se eliminará "${this.selected[0].title}" permanentemente.`
                        : `Se eliminarán ${count} enlaces permanentemente.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Eliminar",
                cancelButtonText: "Cancelar",
                confirmButtonColor: "#e53e3e"
            });

            if (result.isConfirmed) {
                try {
                    for (const item of this.selected) {
                        await this.deleteCustomLink({ id: item.id });
                    }
                    this.selected = [];
                    await this.loadItems();
                } catch (err) {
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Error",
                        text: err.message || "Error al eliminar",
                        icon: "error"
                    });
                }
            }
        },

        toggleReorderMode() {
            this.reorderMode = !this.reorderMode;
            if (this.reorderMode) {
                this.reorderedItems = [...this.items];
            }
        },

        onDragDrop(reorderedItems) {
            this.reorderedItems = reorderedItems.map((item, index) => ({
                ...item,
                weight: index + 1
            }));
        },

        async saveReorder() {
            this.$loading.show();
            try {
                const weightsByType = {};
                this.reorderedItems.forEach((item, index) => {
                    const type = item.type || "link";
                    if (!weightsByType[type]) weightsByType[type] = [];
                    weightsByType[type].push({
                        id: item.id,
                        weight: index + 1
                    });
                });
                await Promise.all(
                    Object.values(weightsByType).map((customLinks) =>
                        this.updateCustomLinksWeights({ customLinks })
                    )
                );
                this.$sound.play();
                this.reorderMode = false;
                await this.loadItems();
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title || "Error",
                    text: error.message || "Error al guardar el orden",
                    icon: "error"
                });
            }
        },

        openCreateModal() {
            this.editingItem = null;
            this.showModal = true;
        },

        onRowAction({ action, item }) {
            if (action === "edit") {
                this.editingItem = item;
                this.showModal = true;
            } else if (action === "delete") {
                this.confirmDelete(item);
            }
        },

        onLinkCreated(link) {
            if (!link) return;
            this.items = [...this.items, link];
        },

        onLinkUpdated(link) {
            if (!link) return;
            this.items = this.items.map((item) =>
                item.id === link.id ? { ...item, ...link } : item
            );
        },

        async confirmDelete(item) {
            const result = await this.$swal.fire({
                background: this.backgroundColor,
                title: "¿Eliminar enlace?",
                text: `Se eliminará "${item.title}" permanentemente.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Eliminar",
                cancelButtonText: "Cancelar",
                confirmButtonColor: "#e53e3e"
            });

            if (result.isConfirmed) {
                try {
                    await this.deleteCustomLink({ id: item.id });
                    await this.loadItems();
                } catch (err) {
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Error",
                        text: err.message || "Error al eliminar",
                        icon: "error"
                    });
                }
            }
        }
    }
};
</script>
